/**
 * Programın yerine ve tarihine göre hava durumu.
 *
 * Üst başlıkta tek satır: hava, yer adı ve hangi güne ait olduğu.
 * Dokununca bugünden başlayan bir haftalık döküm açılıyor; programın
 * günleri orada işaretli duruyor ki kişi "benim günlerim hangileri"
 * diye tarih saymasın.
 *
 * Kural: tarih tahmin ufkunun (16 gün) ötesindeyse o güne dair bir şey
 * söylenemez, ANLIK hava gösteriliyor. Tarih ufka girince O GÜNÜN
 * tahmini, program başlayınca yine ANLIK — kampta olan kişi yarını
 * değil şu anı merak eder.
 *
 * Servis: Open-Meteo. Anahtar istemiyor, ticari olmayan kullanımda
 * ücretsiz. Konum programın kendi enlem/boylamından; yer adıyla
 * aranmıyor çünkü "Çamdağı" coğrafi veritabanlarında yok ve en yakın
 * köyün adı 8 km ötede, 800 metre aşağıda kalıyor.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import type { Event } from '@/lib/types';
import { cn } from '@/lib/utils';

/* Open-Meteo hava kodları — WMO. Kısa tutuldu: üst başlıkta bir
   satırlık yer var, "hafif sağanak yağmurlu" oraya sığmıyor. */
const KOD: Record<number, { ad: string; simge: string }> = {
  0: { ad: 'Açık', simge: '☀️' },
  1: { ad: 'Az bulutlu', simge: '🌤️' },
  2: { ad: 'Parçalı bulutlu', simge: '⛅' },
  3: { ad: 'Kapalı', simge: '☁️' },
  45: { ad: 'Sisli', simge: '🌫️' },
  48: { ad: 'Kırağılı sis', simge: '🌫️' },
  51: { ad: 'Çisenti', simge: '🌦️' },
  53: { ad: 'Çisenti', simge: '🌦️' },
  55: { ad: 'Yoğun çisenti', simge: '🌦️' },
  61: { ad: 'Hafif yağmur', simge: '🌦️' },
  63: { ad: 'Yağmur', simge: '🌧️' },
  65: { ad: 'Kuvvetli yağmur', simge: '🌧️' },
  71: { ad: 'Hafif kar', simge: '🌨️' },
  73: { ad: 'Kar', simge: '❄️' },
  75: { ad: 'Yoğun kar', simge: '❄️' },
  80: { ad: 'Sağanak', simge: '🌦️' },
  81: { ad: 'Sağanak', simge: '🌧️' },
  82: { ad: 'Kuvvetli sağanak', simge: '⛈️' },
  95: { ad: 'Gök gürültülü', simge: '⛈️' },
  96: { ad: 'Dolu', simge: '⛈️' },
  99: { ad: 'Dolu', simge: '⛈️' },
};

const kodAdi = (k?: number) => (k == null ? { ad: '—', simge: '🌡️' } : KOD[k] ?? { ad: '—', simge: '🌡️' });

/** Open-Meteo'nun günlük tahmin ufku. */
const UFUK_GUN = 16;
/** Dökümde kaç gün gösterilecek: bugün + bir hafta. */
const DOKUM_GUN = 8;

const gun = (t: string) => t.slice(0, 10);
/* Yerel takvim günü. toISOString UTC'ye çeviriyor ve Türkiye'de
   gece yarısından sonra bir önceki günü veriyordu. */
const bugun = () => new Date().toLocaleDateString('sv-SE');

const kisaTarih = (t: string) =>
  new Date(t + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
const gunAdi = (t: string) =>
  new Date(t + 'T12:00:00').toLocaleDateString('tr-TR', { weekday: 'short' });

interface Gunluk {
  tarih: string;
  kod: number;
  enYuksek: number;
  enDusuk: number;
  yagis?: number;
}

interface Hava {
  anlikKod?: number;
  anlikSicaklik?: number;
  /** Servisin verdiği ölçüm anı, "YYYY-MM-DDTHH:MM" — kampın saatiyle. */
  guncelleme?: string;
  gunler: Gunluk[];
}

/**
 * Ölçüm anı. Bugünse yalnız saat, değilse tarih de.
 *
 * Metin servisin verdiği gibi kesilip yazılıyor, Date'e çevrilmiyor:
 * timezone=auto ile saat zaten kampın saati; tarayıcının saat dilimine
 * çevirmek başka bir yerdeyken yanlış saati gösterirdi.
 */
function olcumAni(t: string): string {
  const [g, s] = t.split('T');
  const saat = (s ?? '').slice(0, 5);
  return g === bugun() ? saat : `${kisaTarih(g)} ${saat}`;
}

async function havaAl(lat: number, lon: number): Promise<Hava | null> {
  const u = new URL('https://api.open-meteo.com/v1/forecast');
  u.searchParams.set('latitude', String(lat));
  u.searchParams.set('longitude', String(lon));
  u.searchParams.set('current', 'temperature_2m,weather_code');
  u.searchParams.set('daily', 'weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max');
  u.searchParams.set('timezone', 'auto');
  u.searchParams.set('forecast_days', String(UFUK_GUN));

  const c = await fetch(u.toString());
  if (!c.ok) return null;
  const d = await c.json();

  const gunler: Gunluk[] = (d.daily?.time ?? []).map((t: string, i: number) => ({
    tarih: t,
    kod: d.daily.weather_code[i],
    enYuksek: Math.round(d.daily.temperature_2m_max[i]),
    enDusuk: Math.round(d.daily.temperature_2m_min[i]),
    yagis: d.daily.precipitation_probability_max?.[i],
  }));

  return {
    anlikKod: d.current?.weather_code,
    anlikSicaklik: Math.round(d.current?.temperature_2m),
    guncelleme: d.current?.time,
    gunler,
  };
}

/** Programın hangi gününe bakılacak: sürüyorsa bugüne (anlık), gelecekse ilk güne. */
function hedefGun(event?: Event): string | undefined {
  if (!event?.startDate) return undefined;
  const b = bugun();
  const bas = gun(event.startDate);
  const bit = event.endDate ? gun(event.endDate) : bas;
  if (b >= bas && b <= bit) return undefined; // kamp sürüyor → anlık
  return bas > b ? bas : undefined;
}

const programGunuMu = (t: string, event?: Event) => {
  if (!event?.startDate) return false;
  const bas = gun(event.startDate);
  const bit = event.endDate ? gun(event.endDate) : bas;
  return t >= bas && t <= bit;
};

export function HavaDurumu({ event, className }: { event?: Event; className?: string }) {
  const lat = event?.latitude;
  const lon = event?.longitude;
  const [acik, setAcik] = useState(false);
  const kap = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['hava', lat, lon],
    queryFn: () => havaAl(lat!, lon!),
    enabled: lat != null && lon != null,
    /* Hava on dakikada bir değişmiyor; tahmin günde birkaç kez
       güncelleniyor. Yarım saat taze sayılıyor. */
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  /* Dışarı dokununca ve Esc ile kapanıyor. */
  useEffect(() => {
    if (!acik) return;
    const disari = (e: PointerEvent) => {
      if (!kap.current?.contains(e.target as Node)) setAcik(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAcik(false);
    document.addEventListener('pointerdown', disari);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', disari);
      document.removeEventListener('keydown', esc);
    };
  }, [acik]);

  if (!data) return null;

  const hedef = hedefGun(event);
  const hedefGunu = hedef ? data.gunler.find((g) => g.tarih === hedef) : undefined;
  const anlik = !hedefGunu;
  const k = kodAdi(anlik ? data.anlikKod : hedefGunu!.kod);
  const dokum = data.gunler.slice(0, DOKUM_GUN);

  return (
    <div ref={kap} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setAcik((a) => !a)}
        aria-expanded={acik}
        className="flex items-center gap-1.5 text-sm hover:opacity-100 opacity-90 transition-opacity"
      >
        <span aria-hidden className="text-base leading-none">{k.simge}</span>
        {anlik ? (
          <span className="font-medium">{data.anlikSicaklik}°</span>
        ) : (
          <span className="font-medium">
            {hedefGunu!.enYuksek}°<span className="opacity-60">/{hedefGunu!.enDusuk}°</span>
          </span>
        )}
        {event?.locationName && (
          <span className="inline-flex items-center gap-0.5 opacity-80">
            <MapPin className="h-3 w-3" aria-hidden />
            {event.locationName}
          </span>
        )}
        <span className="opacity-70">· {anlik ? 'şimdi' : kisaTarih(hedef!)}</span>
      </button>

      {acik && (
        <div
          className="absolute left-0 top-full z-40 mt-2 w-64 rounded-xl border border-border bg-white p-2 text-primary shadow-lg"
          role="dialog"
          aria-label="Hava durumu dökümü"
        >
          <p className="px-2 pb-1 pt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
            {event?.locationName ?? 'Program yeri'}
          </p>
          <ul className="space-y-0.5">
            {dokum.map((g) => {
              const kampGunu = programGunuMu(g.tarih, event);
              const gk = kodAdi(g.kod);
              return (
                <li
                  key={g.tarih}
                  className={cn(
                    'flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm',
                    /* Programın günleri belirteçli: kişi tarih saymadan
                       kendi günlerini görsün. */
                    kampGunu && 'bg-accent/15 ring-1 ring-accent/40',
                  )}
                >
                  {/* Gün ve tarih tek sütunda ve tek esneyen öge: satır
                      dolduğunda daralan burası oluyor. Ayrı iki sabit
                      sütunken yağış yüzdesi ve "kamp" belirteci birlikte
                      düştüğü günlerde satır kutunun dışına taşıyordu. */}
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                    {g.tarih === bugun() ? 'Bugün' : gunAdi(g.tarih)} {kisaTarih(g.tarih)}
                  </span>
                  <span aria-hidden className="shrink-0">{gk.simge}</span>
                  <span className="shrink-0 whitespace-nowrap font-medium tabular-nums">
                    {g.enYuksek}°<span className="opacity-55">/{g.enDusuk}°</span>
                  </span>
                  {g.yagis != null && g.yagis >= 20 && (
                    <span className="shrink-0 text-[11px] text-sky-700">%{g.yagis}</span>
                  )}
                  {kampGunu && (
                    <span className="shrink-0 rounded-full bg-accent/30 px-1.5 py-0.5 text-[10px] font-medium">
                      kamp
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="px-2 pb-0.5 pt-1.5 text-[10px] leading-snug text-muted-foreground">
            Open-Meteo tahmini.
            {data.guncelleme ? ` Son güncelleme ${olcumAni(data.guncelleme)}.` : ''}
          </p>
        </div>
      )}
    </div>
  );
}
